/**
 * DocumentsAdminPage.jsx
 * ──────────────────────
 * Shows all uploaded documents with their attached scheme and option.
 * Admin can:
 *   - Preview and download any document
 *   - Reassign a document to a different scheme or option
 *   - Delete a document
 */

import { useEffect, useRef, useState } from "react";
import { BASE, getToken, listSchemes, deleteAttachedDoc } from "../../services/api";

async function fetchAllDocuments() {
  // List all documents by fetching without scheme_id filter (admin only endpoint)
  const res  = await fetch(`${BASE}/schemes/documents`, {
    headers: { "Authorization": `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function fetchBlob(docId, endpoint) {
  const res = await fetch(`${BASE}/schemes/documents/${docId}/${endpoint}`, {
    headers: { "Authorization": `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.blob();
}

async function downloadDoc(doc) {
  const blob = await fetchBlob(doc.id, "download");
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = doc.filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

async function reassignDoc(docId, schemeId, optionText, optionMeasure) {
  const res = await fetch(`${BASE}/schemes/documents/${docId}/reassign`, {
    method:  "PUT",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
    body:    JSON.stringify({
      scheme_id:      schemeId      || null,
      option_text:    optionText    || null,
      option_measure: optionMeasure || null,
    }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail ?? "Reassign failed");
  }
  return res.json();
}

const BADGE = {
  pdf:  { label:"PDF", bg:"#FCEBEB", color:"#A32D2D" },
  docx: { label:"DOC", bg:"#E6F1FB", color:"#185FA5" },
  doc:  { label:"DOC", bg:"#E6F1FB", color:"#185FA5" },
  txt:  { label:"TXT", bg:"#EAF3DE", color:"#3B6D11" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });
}

function ReassignForm({ doc, schemes, onDone, onCancel }) {
  const [selScheme,    setSelScheme]    = useState(doc.scheme_id ?? "");
  const [options,      setOptions]      = useState([]);
  // "measure|||option_text" combined key
  const initKey = doc.option_text
    ? `${doc.option_measure || ""}|||${doc.option_text}`
    : "";
  const [selOptionKey, setSelOptionKey] = useState(initKey);
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState("");

  async function loadOptions(schemeId) {
    setSelOptionKey(""); setOptions([]);
    if (!schemeId) return;
    try {
      const res  = await fetch(`${BASE}/schemes/${schemeId}/options`,
        { headers: { "Authorization": `Bearer ${getToken()}` } });
      setOptions(await res.json());
    } catch { setOptions([]); }
  }

  useEffect(() => { if (selScheme) loadOptions(selScheme); }, []);

  async function save() {
    setSaving(true); setMsg("");
    const [optMeasure, ...rest] = selOptionKey ? selOptionKey.split("|||") : ["",""];
    const optText = rest.join("|||");
    try {
      await reassignDoc(doc.id, selScheme, optText || null, optMeasure || null);
      setMsg("Saved ✓");
      setTimeout(onDone, 800);
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  }

  // Group schemes by crop for the dropdown
  const grouped = {};
  schemes.forEach((s) => {
    const crop = s.crop_id;
    if (!grouped[crop]) grouped[crop] = [];
    grouped[crop].push(s);
  });

  return (
    <div style={{ padding: "10px 12px", background: "#f8f9fa",
      borderTop: "1px solid #dee2e6" }}>
      {msg && <div style={{ fontSize: 12, marginBottom: 6,
        color: msg.includes("✓") ? "#3B6D11" : "#A32D2D" }}>{msg}</div>}
      <div className="d-flex gap-2 flex-wrap align-items-end">
        <div>
          <label style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 3 }}>Scheme</label>
          <select className="form-select form-select-sm" style={{ fontSize: 12, width: 280 }}
            value={selScheme}
            onChange={(e) => { setSelScheme(e.target.value); loadOptions(e.target.value); }}>
            <option value="">No scheme</option>
            {Object.entries(grouped).map(([crop, items]) => (
              <optgroup key={crop} label={crop.replace("_"," ")}>
                {items.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 500, display: "block", marginBottom: 3 }}>
            Option <span style={{ fontWeight: 400, color: "#adb5bd" }}>(optional)</span>
          </label>
          <select className="form-select form-select-sm" style={{ fontSize: 12, width: 200 }}
            value={selOptionKey} disabled={!selScheme}
            onChange={(e) => setSelOptionKey(e.target.value)}>
            <option value="">General (scheme level)</option>
            {["preventive","monitoring","intervention"].map((m) => {
              const opts = options.filter((o) => o.measure === m);
              return opts.length === 0 ? null : (
                <optgroup key={m} label={m.charAt(0).toUpperCase()+m.slice(1)}>
                  {opts.map((o) => (
                    <option key={o.id} value={`${m}|||${o.option_text}`}>{o.option_text}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
        <button className="btn btn-sm btn-primary" style={{ fontSize: 12 }}
          onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }}
          onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function DocumentsAdminPage() {
  const [docs,      setDocs]      = useState([]);
  const [schemes,   setSchemes]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [reassigning, setReassigning] = useState(null);  // doc.id
  const [previewing,  setPreviewing]  = useState(null);  // { url, filename }

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    try {
      const [d, s] = await Promise.all([fetchAllDocuments(), listSchemes()]);
      setDocs(d);
      setSchemes(s);
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }

  async function handlePreview(doc) {
    try {
      const blob = await fetchBlob(doc.id, "preview");
      const url  = URL.createObjectURL(blob);
      setPreviewing({ url, filename: doc.title || doc.filename });
    } catch (e) { alert("Preview failed: " + e.message); }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try { await deleteAttachedDoc(doc.id); await load(); }
    catch (e) { setError(e.message); }
  }

  // Enrich docs with scheme label
  const schemeMap = {};
  schemes.forEach((s) => { schemeMap[s.id] = s; });

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Documents</h2>
          <p style={{ fontSize: 13, color: "#6c757d", margin: "4px 0 0" }}>
            All reference documents attached to schemes or options.
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {error   && <div className="alert alert-danger py-2 mb-3" style={{fontSize:13}}>{error}</div>}
      {loading && <div className="text-muted" style={{fontSize:14}}>Loading…</div>}

      {!loading && docs.length === 0 && (
        <div className="text-center text-muted py-5 rounded"
          style={{ border:"1px dashed #dee2e6", background:"#fff", fontSize:13 }}>
          No documents uploaded yet. Use the Schemes page to attach documents.
        </div>
      )}

      {!loading && docs.length > 0 && (
        <div style={{ background:"#fff", border:"1px solid #dee2e6",
          borderRadius:12, overflow:"hidden" }}>
          {docs.map((doc, idx) => {
            const b      = BADGE[doc.file_type] ?? { label:doc.file_type?.toUpperCase(), bg:"#eee", color:"#333" };
            const scheme = schemeMap[doc.scheme_id];
            const schemeDisplay = scheme
              ? (scheme.id.includes("_") ? scheme.id.split("_").slice(1).join("_") : scheme.id)
              : null;

            return (
              <div key={doc.id}>
                {idx > 0 && <div style={{ borderTop:"1px solid #f1f3f5" }} />}
                <div style={{ padding:"12px 16px" }}>
                  <div className="d-flex align-items-start gap-3 flex-wrap">
                    {/* Type badge */}
                    <span style={{ fontSize:11, fontWeight:600, padding:"2px 7px",
                      borderRadius:4, background:b.bg, color:b.color,
                      flexShrink:0, marginTop:2 }}>
                      {b.label}
                    </span>

                    {/* Main info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:"#212529" }}>
                        {doc.title || doc.filename}
                      </div>
                      {doc.title && (
                        <div style={{ fontSize:11, color:"#adb5bd" }}>{doc.filename}</div>
                      )}
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {scheme ? (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:4,
                            background:"#E6F1FB", color:"#185FA5" }}>
                            Scheme: {schemeDisplay}
                          </span>
                        ) : (
                          <span style={{ fontSize:11, color:"#adb5bd" }}>No scheme assigned</span>
                        )}
                        {doc.option_text && (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:4,
                            background:"#FAEEDA", color:"#633806" }}>
                            {doc.option_measure
                              ? `${doc.option_measure.charAt(0).toUpperCase()}${doc.option_measure.slice(1)}: ${doc.option_text}`
                              : doc.option_text}
                          </span>
                        )}
                        <span style={{ fontSize:11, color:"#adb5bd" }}>
                          Uploaded {fmtDate(doc.uploaded_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-1 flex-wrap">
                      {doc.file_type === "pdf" && (
                        <button className="btn btn-sm btn-outline-secondary"
                          style={{ fontSize:11 }} onClick={() => handlePreview(doc)}>
                          Preview
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-primary"
                        style={{ fontSize:11 }} onClick={() => downloadDoc(doc)}>
                        Download
                      </button>
                      <button className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize:11 }}
                        onClick={() => setReassigning(reassigning === doc.id ? null : doc.id)}>
                        {reassigning === doc.id ? "Cancel" : "Reassign"}
                      </button>
                      <button className="btn btn-sm btn-outline-danger"
                        style={{ fontSize:11 }} onClick={() => handleDelete(doc)}>
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Inline reassign form */}
                  {reassigning === doc.id && (
                    <div style={{ marginTop:8 }}>
                      <ReassignForm
                        doc={doc}
                        schemes={schemes}
                        onDone={() => { setReassigning(null); load(); }}
                        onCancel={() => setReassigning(null)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PDF preview modal */}
      {previewing && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
          zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setPreviewing(null)}>
          <div style={{ background:"#fff", borderRadius:12, padding:16,
            width:"90vw", maxWidth:960, height:"88vh",
            display:"flex", flexDirection:"column" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize:13, fontWeight:500 }}>{previewing.filename}</span>
              <button onClick={() => setPreviewing(null)}
                style={{ background:"none", border:"none", fontSize:18,
                  cursor:"pointer", color:"#6c757d" }}>✕</button>
            </div>
            <iframe src={previewing.url} title={previewing.filename}
              style={{ flex:1, border:"1px solid #dee2e6", borderRadius:8 }} />
          </div>
        </div>
      )}
    </div>
  );
}
